/**
 * Error Types and Messages Constants
 * Centralized error handling for the OFC application
 */

// Error Type Enums
export const ERROR_TYPES = {
  // Network Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',

  // Server Errors
  SERVER_ERROR: 'SERVER_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Authentication Errors
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  ACCESS_REVOKED: 'ACCESS_REVOKED',
  INVALID_TOKEN: 'INVALID_TOKEN',

  // Account Errors
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  AWAITING_VERIFICATION: 'AWAITING_VERIFICATION',
  KYC_PENDING: 'KYC_PENDING',
  KYC_REJECTED: 'KYC_REJECTED',

  // Rate Limiting
  RATE_LIMITED: 'RATE_LIMITED',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',

  // Validation Errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',

  // Resource Errors
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_DELETED: 'RESOURCE_DELETED',
  RESOURCE_UNAVAILABLE: 'RESOURCE_UNAVAILABLE',

  // Permission Errors
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',

  // Generic
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

// HTTP Status to Error Type mapping
export const HTTP_STATUS_ERROR_MAP = {
  400: ERROR_TYPES.VALIDATION_ERROR,
  401: ERROR_TYPES.UNAUTHORIZED,
  403: ERROR_TYPES.FORBIDDEN,
  404: ERROR_TYPES.NOT_FOUND,
  408: ERROR_TYPES.NETWORK_TIMEOUT,
  429: ERROR_TYPES.TOO_MANY_REQUESTS,
  500: ERROR_TYPES.SERVER_ERROR,
  502: ERROR_TYPES.SERVICE_UNAVAILABLE,
  503: ERROR_TYPES.SERVICE_UNAVAILABLE,
  504: ERROR_TYPES.NETWORK_TIMEOUT,
};

// Error Messages - values are translation keys (resolve with t() in components)
export const ERROR_MESSAGES = {
  // Network Errors
  [ERROR_TYPES.NETWORK_ERROR]: {
    titleKey: 'errors.networkError.title',
    messageKey: 'errors.networkError.message',
    actionKey: 'errors.networkError.action',
    recoverable: true,
  },
  [ERROR_TYPES.NETWORK_TIMEOUT]: {
    titleKey: 'errors.networkTimeout.title',
    messageKey: 'errors.networkTimeout.message',
    actionKey: 'errors.networkTimeout.action',
    recoverable: true,
  },
  [ERROR_TYPES.NETWORK_OFFLINE]: {
    titleKey: 'errors.networkOffline.title',
    messageKey: 'errors.networkOffline.message',
    actionKey: 'errors.networkOffline.action',
    recoverable: true,
  },

  // Server Errors
  [ERROR_TYPES.SERVER_ERROR]: {
    titleKey: 'errors.serverError.title',
    messageKey: 'errors.serverError.message',
    actionKey: 'errors.serverError.action',
    recoverable: true,
  },
  [ERROR_TYPES.DATABASE_ERROR]: {
    titleKey: 'errors.databaseError.title',
    messageKey: 'errors.databaseError.message',
    actionKey: 'errors.databaseError.action',
    recoverable: true,
  },
  [ERROR_TYPES.SERVICE_UNAVAILABLE]: {
    titleKey: 'errors.serviceUnavailable.title',
    messageKey: 'errors.serviceUnavailable.message',
    actionKey: 'errors.serviceUnavailable.action',
    recoverable: true,
  },

  // Authentication Errors
  [ERROR_TYPES.SESSION_EXPIRED]: {
    titleKey: 'errors.sessionExpired.title',
    messageKey: 'errors.sessionExpired.message',
    actionKey: 'errors.sessionExpired.action',
    recoverable: true,
    redirectTo: '/login',
  },
  [ERROR_TYPES.UNAUTHORIZED]: {
    titleKey: 'errors.unauthorized.title',
    messageKey: 'errors.unauthorized.message',
    actionKey: 'errors.unauthorized.action',
    recoverable: true,
    redirectTo: '/login',
  },
  [ERROR_TYPES.ACCESS_REVOKED]: {
    titleKey: 'errors.accessRevoked.title',
    messageKey: 'errors.accessRevoked.message',
    actionKey: 'errors.accessRevoked.action',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.INVALID_TOKEN]: {
    titleKey: 'errors.invalidToken.title',
    messageKey: 'errors.invalidToken.message',
    actionKey: 'errors.invalidToken.action',
    recoverable: true,
    redirectTo: '/login',
  },

  // Account Errors
  [ERROR_TYPES.ACCOUNT_SUSPENDED]: {
    titleKey: 'errors.accountSuspended.title',
    messageKey: 'errors.accountSuspended.message',
    actionKey: 'errors.accountSuspended.action',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.ACCOUNT_LOCKED]: {
    titleKey: 'errors.accountLocked.title',
    messageKey: 'errors.accountLocked.message',
    actionKey: 'errors.accountLocked.action',
    recoverable: false,
    redirectTo: '/support',
  },
  [ERROR_TYPES.ACCOUNT_DELETED]: {
    titleKey: 'errors.accountDeleted.title',
    messageKey: 'errors.accountDeleted.message',
    actionKey: 'errors.accountDeleted.action',
    recoverable: false,
    redirectTo: '/register',
  },
  [ERROR_TYPES.AWAITING_VERIFICATION]: {
    titleKey: 'errors.awaitingVerification.title',
    messageKey: 'errors.awaitingVerification.message',
    actionKey: 'errors.awaitingVerification.action',
    recoverable: true,
  },
  [ERROR_TYPES.KYC_PENDING]: {
    titleKey: 'errors.kycPending.title',
    messageKey: 'errors.kycPending.message',
    actionKey: 'errors.kycPending.action',
    recoverable: true,
  },
  [ERROR_TYPES.KYC_REJECTED]: {
    titleKey: 'errors.kycRejected.title',
    messageKey: 'errors.kycRejected.message',
    actionKey: 'errors.kycRejected.action',
    recoverable: true,
    redirectTo: '/profile',
  },

  // Rate Limiting
  [ERROR_TYPES.RATE_LIMITED]: {
    titleKey: 'errors.rateLimited.title',
    messageKey: 'errors.rateLimited.message',
    actionKey: 'errors.rateLimited.action',
    recoverable: true,
  },
  [ERROR_TYPES.TOO_MANY_REQUESTS]: {
    titleKey: 'errors.tooManyRequests.title',
    messageKey: 'errors.tooManyRequests.message',
    actionKey: 'errors.tooManyRequests.action',
    recoverable: true,
  },

  // Validation Errors
  [ERROR_TYPES.VALIDATION_ERROR]: {
    titleKey: 'errors.validationError.title',
    messageKey: 'errors.validationError.message',
    actionKey: 'errors.validationError.action',
    recoverable: true,
  },
  [ERROR_TYPES.INVALID_INPUT]: {
    titleKey: 'errors.invalidInput.title',
    messageKey: 'errors.invalidInput.message',
    actionKey: 'errors.invalidInput.action',
    recoverable: true,
  },
  [ERROR_TYPES.MISSING_REQUIRED_FIELD]: {
    titleKey: 'errors.missingRequiredField.title',
    messageKey: 'errors.missingRequiredField.message',
    actionKey: 'errors.missingRequiredField.action',
    recoverable: true,
  },

  // Resource Errors
  [ERROR_TYPES.NOT_FOUND]: {
    titleKey: 'errors.notFound.title',
    messageKey: 'errors.notFound.message',
    actionKey: 'errors.notFound.action',
    recoverable: true,
  },
  [ERROR_TYPES.RESOURCE_DELETED]: {
    titleKey: 'errors.resourceDeleted.title',
    messageKey: 'errors.resourceDeleted.message',
    actionKey: 'errors.resourceDeleted.action',
    recoverable: false,
  },
  [ERROR_TYPES.RESOURCE_UNAVAILABLE]: {
    titleKey: 'errors.resourceUnavailable.title',
    messageKey: 'errors.resourceUnavailable.message',
    actionKey: 'errors.resourceUnavailable.action',
    recoverable: true,
  },

  // Permission Errors
  [ERROR_TYPES.FORBIDDEN]: {
    titleKey: 'errors.forbidden.title',
    messageKey: 'errors.forbidden.message',
    actionKey: 'errors.forbidden.action',
    recoverable: false,
  },
  [ERROR_TYPES.INSUFFICIENT_PERMISSIONS]: {
    titleKey: 'errors.insufficientPermissions.title',
    messageKey: 'errors.insufficientPermissions.message',
    actionKey: 'errors.insufficientPermissions.action',
    recoverable: false,
  },

  // Generic
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    titleKey: 'errors.unknownError.title',
    messageKey: 'errors.unknownError.message',
    actionKey: 'errors.unknownError.action',
    recoverable: true,
  },
};

/**
 * Get error type from HTTP status code
 * @param {number} statusCode - HTTP status code
 * @returns {string} Error type
 */
export function getErrorTypeFromStatus(statusCode) {
  return HTTP_STATUS_ERROR_MAP[statusCode] || ERROR_TYPES.UNKNOWN_ERROR;
}

/**
 * Get error config from error type
 * @param {string} errorType - Error type from ERROR_TYPES
 * @returns {Object} Error configuration
 */
export function getErrorConfig(errorType) {
  return ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
}

/**
 * Parse API error response and return standardized error object
 * @param {Error|Object} error - Error object from API call
 * @returns {Object} Standardized error object
 */
export function parseApiError(error) {
  // Handle network errors
  if (!navigator.onLine) {
    return {
      type: ERROR_TYPES.NETWORK_OFFLINE,
      ...ERROR_MESSAGES[ERROR_TYPES.NETWORK_OFFLINE],
    };
  }

  if (error?.name === 'TypeError' || error?.message?.includes('fetch')) {
    return {
      type: ERROR_TYPES.NETWORK_ERROR,
      ...ERROR_MESSAGES[ERROR_TYPES.NETWORK_ERROR],
    };
  }

  // Handle HTTP status codes
  const status = error?.status || error?.response?.status;
  if (status) {
    // Special handling for specific error messages from backend
    // Prefer backend-provided message when available (response.data.message or response.data.error.message)
    const errorMessage =
      error?.response?.data?.message || error?.response?.data?.error?.message || error?.message;

    if (status === 401) {
      if (errorMessage?.toLowerCase().includes('expired')) {
        return {
          type: ERROR_TYPES.SESSION_EXPIRED,
          ...ERROR_MESSAGES[ERROR_TYPES.SESSION_EXPIRED],
          originalMessage: errorMessage,
        };
      }
      if (errorMessage?.toLowerCase().includes('revoked')) {
        return {
          type: ERROR_TYPES.ACCESS_REVOKED,
          ...ERROR_MESSAGES[ERROR_TYPES.ACCESS_REVOKED],
          originalMessage: errorMessage,
        };
      }
    }

    if (status === 403) {
      if (errorMessage?.toLowerCase().includes('suspended')) {
        return {
          type: ERROR_TYPES.ACCOUNT_SUSPENDED,
          ...ERROR_MESSAGES[ERROR_TYPES.ACCOUNT_SUSPENDED],
          originalMessage: errorMessage,
        };
      }
      if (errorMessage?.toLowerCase().includes('verification')) {
        return {
          type: ERROR_TYPES.AWAITING_VERIFICATION,
          ...ERROR_MESSAGES[ERROR_TYPES.AWAITING_VERIFICATION],
          originalMessage: errorMessage,
        };
      }
    }

    const errorType = getErrorTypeFromStatus(status);
    return {
      type: errorType,
      ...ERROR_MESSAGES[errorType],
      originalMessage: errorMessage,
    };
  }

  // Default to unknown error
  return {
    type: ERROR_TYPES.UNKNOWN_ERROR,
    ...ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR],
    originalMessage: error?.message,
  };
}

export default ERROR_TYPES;
